import { Enums } from "@/services/dropdown-enums";

const STORAGE_KEY = "blazeo_self_calendar_participant";

const normalizeEmail = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const isConnectedStatus = (status) =>
  status === Enums.ConnectEmailStatus.Connected ||
  Number(status) === Enums.ConnectEmailStatus.Connected;

const isDirectAuthorizationSource = (member) => {
  const source = member?.authorizationSource ?? member?.AuthorizationSource;
  if (source == null || source === "") return false;
  const normalized = String(source).toLowerCase().replace(/[\s_-]/g, "");
  return (
    normalized === "direct" ||
    normalized === "directauthorization" ||
    normalized === "self"
  );
};

const memberIdOf = (member) =>
  member?.participantId || member?.id || member?.memberId || "";

const storageUserKey = (user) => {
  const userId = user?.UserGUID || user?.userGUID;
  const companyKey = user?.CompanyKey || user?.companyKey;
  if (!userId || !companyKey) return "";
  return `${companyKey}:${userId}`;
};

export const getSelfConnectedParticipantId = (user) => {
  if (typeof window === "undefined") return "";
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    if (!parsed?.participantId || parsed.ownerKey !== storageUserKey(user)) {
      return "";
    }
    return String(parsed.participantId);
  } catch {
    return "";
  }
};

export const rememberSelfConnectedParticipant = (user, participantId) => {
  if (typeof window === "undefined" || !participantId) return;
  const ownerKey = storageUserKey(user);
  if (!ownerKey) return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ownerKey, participantId: String(participantId) })
    );
  } catch {
    // ignore quota / private mode
  }
};

export const clearSelfConnectedParticipant = (user, participantId) => {
  if (typeof window === "undefined") return;
  const stored = getSelfConnectedParticipantId(user);
  if (participantId && stored && String(stored) !== String(participantId)) {
    return;
  }
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

const belongsToLoggedInUser = (member, user) => {
  const loggedInUserId = user?.UserGUID || user?.userGUID;
  const authBy =
    member?.AuthorizedByParticipantId || member?.authorizedByParticipantId;
  if (loggedInUserId && authBy && String(authBy) === String(loggedInUserId)) {
    return true;
  }

  const profileEmails = [
    user?.Email,
    user?.email,
    user?.SSOEmail,
    user?.ssoEmail,
  ]
    .map(normalizeEmail)
    .filter(Boolean);
  const memberEmails = [
    member?.email,
    member?.Email,
    member?.userSsoEmail,
    member?.UserSsoEmail,
    member?.userSSOEmail,
  ]
    .map(normalizeEmail)
    .filter(Boolean);
  return memberEmails.some((email) => profileEmails.includes(email));
};

/**
 * The logged-in user's Connect Calendar / Get Started member only.
 * Invites (including an invite of the login email) and other users' connects are excluded.
 */
export const isSelfConnectedMember = (member, user) => {
  if (!member || !user || !isConnectedStatus(member.status)) return false;

  const storedId = getSelfConnectedParticipantId(user);
  const memberId = String(memberIdOf(member));
  if (storedId && memberId && storedId === memberId) return true;

  if (!isDirectAuthorizationSource(member)) return false;
  return belongsToLoggedInUser(member, user);
};

export const findConnectedSelfMember = (members = [], user) =>
  members.find((member) => isSelfConnectedMember(member, user));
